import React, { useState, useEffect } from 'react';
import { Card, Container, Button } from 'react-bootstrap';
import './DashboardComponent.css';
import { useHistory } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { areaService, AreaNotaHistoricoResponse, Area } from '../../services/areaService';
import { questionService, HistoricoScoreResponse } from '../../services/questionService';

// ===== TYPES =====

export interface YearProgressionData {
    area: string;
    years: Array<{
        year: number;
        score: number;
        change: number;
        changePercentage: number;
    }>;
}

export type TableItemType = 'Questao' | 'Item' | 'Acao';

export interface TableItem {
    id: string;
    title: string;
    description: string;
    years: Array<{
        year: number;
        score: number;
        variation: number;
    }>;
}

export const dashboardService = {
    async getYearProgressionData(entityId: string, areaId: string): Promise<YearProgressionData> {
        try {
            const response: AreaNotaHistoricoResponse = await areaService.getAreaNotaHistorico(entityId, areaId);

            const transformedData: YearProgressionData = {
                area: response.area,
                years: response.anos.map((ano, index) => ({
                    year: ano.ano,
                    score: ano.nota,
                    change: ano.variacao,
                    changePercentage: ano.percentual_variacao
                }))
            };

            return transformedData;
        } catch (error) {
            console.error('Error fetching year progression data:', error);
            throw error;
        }
    },

    async getTableData(type: TableItemType, entityId: string, areaId: string): Promise<TableItem[]> {
        try {
            const tipoMap: Record<TableItemType, string> = {
                'Questao': 'question',
                'Item': 'item',
                'Acao': 'action'
            };

            const tipo = tipoMap[type];

            const response: HistoricoScoreResponse[] = await questionService.getScoreHistory(entityId, areaId, tipo);

            const transformedData: TableItem[] = response.map(item => ({
                id: item.id,
                title: item.titulo,
                description: item.descricao,
                years: item.historico.map(hist => ({
                    year: hist.ano,
                    score: hist.nota,
                    variation: hist.variacao
                }))
            }));

            return transformedData;
        } catch (error) {
            console.error('Error fetching table data:', error);
            return [];
        }
    }
};

const YearProgressionCard: React.FC<{
    data: YearProgressionData;
}> = ({ data }) => {
    const maxScore = 10;

    return (
        <Card className="h-100 modern-progression-card">
            <Card.Body>
                <h5 className="mb-4 text-center fw-bold">{data.area} - Progresso Anual</h5>
                <div className="year-progression-chart">
                    {data.years.map((year, index) => {
                        const scorePercentage = (year.score / maxScore) * 100;
                        const isFirstYear = index === 0;

                        return (
                            <div key={year.year} className="year-gauge">
                                {/* Change indicator between years */}
                                {!isFirstYear && (
                                    <div className="change-indicator">
                                        <div className="change-circle">
                                            <span className={`change-value ${year.change >= 0 ? 'positive' : 'negative'}`}>
                                                {year.change >= 0 ? '+' : ''}{year.change.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Main year gauge */}
                                <div className="gauge-container">
                                    <div className="gauge-circle">
                                        <div className="gauge-ring" style={{
                                            background: `conic-gradient(
                                                ${year.change >= 0 ? 'var(--primary-color)' : '#dc3545'} 0deg,
                                                ${year.change >= 0 ? 'var(--primary-color)' : '#dc3545'} ${scorePercentage * 3.6}deg,
                                                #e9ecef ${scorePercentage * 3.6}deg,
                                                #e9ecef 360deg
                                            )`
                                        }}>
                                            <div className="gauge-inner">
                                                <span className="gauge-value">{year.score.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="year-label">{year.year}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card.Body>
        </Card>
    );
};

const DataTableComponent: React.FC<{
    area: string;
    entityId: string;
    areaId: string;
}> = ({ area, entityId, areaId }) => {
    const [selectedType, setSelectedType] = useState<TableItemType>('Questao');
    const [tableData, setTableData] = useState<TableItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadTableData = async () => {
            setLoading(true);
            try {
                const data = await dashboardService.getTableData(selectedType, entityId, areaId);
                setTableData(data);
            } catch (error) {
                console.error('Erro ao carregar dados da tabela:', error);
            } finally {
                setLoading(false);
            }
        };

        loadTableData();
    }, [selectedType, area, entityId, areaId]);

    const years = tableData.length > 0 ? tableData[0].years.map(y => y.year).sort((a, b) => a - b) : [];

    return (
        <Card className="h-100 modern-progression-card">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="mb-0 fw-bold">Dados Detalhados</h5>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Tipo:</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto' }}
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value as TableItemType)}
                        >
                            <option value="Questao">Questão</option>
                            <option value="Item">Item</option>
                            <option value="Acao">Ação</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th style={{ minWidth: '200px' }}>Título</th>
                                    <th style={{ minWidth: '250px' }}>Descrição</th>
                                    {years.map(year => (
                                        <th key={year} className="text-center" style={{ minWidth: '120px' }}>
                                            <div className="d-flex flex-column">
                                                <span className="fw-bold">{year}</span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map(item => (
                                    <tr key={item.id}>
                                        <td className="fw-semibold">{item.title}</td>
                                        <td className="text-muted">{item.description}</td>
                                        {years.map(year => {
                                            // Find the data for this specific year
                                            const yearData = item.years.find(y => y.year === year);
                                            return (
                                                <td key={year} className="text-center">
                                                    {yearData ? (
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <span>
                                                                {yearData.score.toFixed(1)}
                                                            </span>
                                                            <span className={`badge ${yearData.variation >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                {yearData.variation >= 0 ? '+' : ''}{yearData.variation.toFixed(1)}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

const DashboardComponent: React.FC = () => {
    const [yearProgressionData, setYearProgressionData] = useState<YearProgressionData | null>(null);
    const [loading, setLoading] = useState(true);
    const [availableAreas, setAvailableAreas] = useState<Area[]>([]);
    const [selectedArea, setSelectedArea] = useState<Area | null>(null);
    const history = useHistory();
    const { currentEntity } = useEntity();

    useEffect(() => {
        const fetchAvailableAreas = async () => {
            if (currentEntity) {
                try {
                    const areas = await areaService.getAvailableAreas(currentEntity.entidade_id);
                    setAvailableAreas(areas);
                    // Set the first area as selected by default
                    if (areas.length > 0 && !selectedArea) {
                        setSelectedArea(areas[0]);
                    }
                } catch (error) {
                    console.error('Erro ao carregar áreas disponíveis:', error);
                }
            }
        };

        fetchAvailableAreas();
    }, [currentEntity]);

    useEffect(() => {
        const loadYearProgressionData = async () => {
            if (currentEntity && selectedArea) {
                try {
                    const data = await dashboardService.getYearProgressionData(currentEntity.entidade_id, selectedArea.area_id);
                    setYearProgressionData(data);
                } catch (error) {
                    console.error('Erro ao carregar dados de progresso anual:', error);
                }
            }
        };

        loadYearProgressionData();
    }, [currentEntity, selectedArea]);

    useEffect(() => {
        if (yearProgressionData) {
            setLoading(false);
        }
    }, [yearProgressionData]);

    const handleReturn = () => history.push('/home/modulo');

    if (loading || !currentEntity || !selectedArea) {
        return (
            <Container fluid className="dashboard-container p-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    {!currentEntity && <p className="mt-2">Carregando entidade...</p>}
                    {!selectedArea && <p className="mt-2">Carregando área...</p>}
                </div>
            </Container>
        );
    }

    if (!yearProgressionData) {
        return (
            <Container fluid className="dashboard-container p-4">
                <div className="text-center">
                    <p>Erro ao carregar dados do dashboard</p>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="dashboard-container p-4">
            <div className="dashboard-header mb-4">
                <div className="d-flex flex-column gap-3">
                    <Button
                        variant="outline-secondary"
                        onClick={handleReturn}
                        className="return-button d-flex align-items-center gap-2"
                        style={{ width: 'fit-content' }}
                    >
                        <span>←</span> Voltar
                    </Button>
                    <h2>Visualizador Geral</h2>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted fw-semibold">Área:</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', minWidth: '150px' }}
                            value={selectedArea?.area_id || ''}
                            onChange={(e) => {
                                const area = availableAreas.find(a => a.area_id === e.target.value);
                                if (area) {
                                    setSelectedArea(area);
                                }
                            }}
                        >
                            {availableAreas.map(area => (
                                <option key={area.area_id} value={area.area_id}>
                                    {area.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-12">
                    <YearProgressionCard
                        data={yearProgressionData}
                    />
                </div>
            </div>

            <div className="row mt-4">
                <div className="col-12">
                    <DataTableComponent
                        area={selectedArea?.nome || ''}
                        entityId={currentEntity?.entidade_id || ''}
                        areaId={selectedArea?.area_id || ''}
                    />
                </div>
            </div>
        </Container>
    );
};

export default DashboardComponent;
